import { graphqlClient } from '@/lib/graphql-client';
import { mutateGateway } from './gateway-mutate';
import { withUserScope } from '@/lib/user-scope';
import { dateOnlyOf, orgCalendarDate } from '@/lib/utils/custom-date/custom-date';
import { getOrganizationTimeZone } from '@/lib/utils/custom-date/org-time-zone';
import { INSERT_FINE_MUTATION } from '../graphql/mutations';
import {
  buildGetEmployeeFinesQuery,
  buildGetEmployeeInsuranceQuery,
} from '../graphql/profile-view-lookups';

/** Who a MetLife policy covers (legacy `MetLifeApplicationType`). */
type InsuranceType = 'Employee' | 'Spouse' | 'Child1' | 'Child2' | 'Child3';

export type InsuranceIds = Partial<Record<InsuranceType, string>>;

export interface FineTotals {
  lunch: number;
  nonCompliance: number;
}

const PAGE_SIZE = 200;

/** Policy numbers of the employee's completed MetLife applications, by who they cover. */
export const getEmployeeInsuranceIds = async (userId: string): Promise<InsuranceIds> => {
  const result = await graphqlClient.query<{
    MetLifeApplications: {
      items: { TypeOfApplication: InsuranceType; MetLifeIdNumber?: string }[];
    };
  }>({
    query: buildGetEmployeeInsuranceQuery({ UserId: userId, Status: 'Completed' }, 1, PAGE_SIZE),
  });
  const ids: InsuranceIds = {};
  result.MetLifeApplications.items.forEach((item) => {
    if (item.MetLifeIdNumber) ids[item.TypeOfApplication] = item.MetLifeIdNumber;
  });
  return ids;
};

/**
 * Lunch and non-compliance fines charged inside an HR term. (The attendance fine is worked out by the
 * attendance calculation, not stored as a fine record, so it is not part of these totals.)
 */
export const getEmployeeFineTotals = async (
  userId: string,
  periodStart?: string,
  periodEnd?: string
): Promise<FineTotals> => {
  const result = await graphqlClient.query<{
    Fines: { items: { FineType?: string; FineAmount?: number; FineCreatedDateUtc?: string }[] };
  }>({ query: buildGetEmployeeFinesQuery({ UserId: userId }, 1, PAGE_SIZE) });

  const totals: FineTotals = { lunch: 0, nonCompliance: 0 };
  result.Fines.items.forEach((fine) => {
    // The fine's day in the app timezone against the HR term's days (kind A, both bounds inclusive).
    const created = orgCalendarDate(fine.FineCreatedDateUtc);
    if (
      (periodStart && created < dateOnlyOf(periodStart)) ||
      (periodEnd && created > dateOnlyOf(periodEnd))
    )
      return;
    if (fine.FineType === 'LunchFine') totals.lunch += fine.FineAmount ?? 0;
    if (fine.FineType === 'NonComplianceFine') totals.nonCompliance += fine.FineAmount ?? 0;
  });
  return totals;
};

export interface CreateFineParams {
  userId: string;
  /** The signed-in admin issuing the fine (`Fine.FinedBy`). */
  finedBy: string;
  fineType: 'LunchFine' | 'NonComplianceFine';
  fineAmount: number;
  /** When the fine was created, as a UTC ISO instant (kind C). */
  fineCreatedDateUtc: string;
  remarks: string;
}

/**
 * Ad-hoc lunch/non-compliance fine an HR admin issues to an employee from the directory list
 * (legacy `HrFineComponent`/`MakeFineCommand`). The attendance fine, by contrast, is computed
 * automatically from approved correction requests, not created here — see `getMyFineSummary`
 * in hr-my-attendance's attendance.service.ts.
 */
export const createFine = async (params: CreateFineParams) =>
  mutateGateway({
    query: INSERT_FINE_MUTATION,
    variables: {
      input: await withUserScope(params.userId, {
        UserId: params.userId,
        FinedBy: params.finedBy,
        FineType: params.fineType,
        FineAmount: params.fineAmount,
        FineCreatedDateUtc: params.fineCreatedDateUtc,
        Timezone: getOrganizationTimeZone(),
        Remarks: params.remarks,
      }),
    },
  });
